import * as stylex from '@stylexjs/stylex'
import { useState } from "react";

import Avatar from "./Avatar";
import Dialog from "./Dialog";
import Icon from "./Icon";
import {
  centsToInput,
  currencyOptions,
  findPerson,
  parseCents,
  type CreateCurrencyExchangeInput,
  type Overview,
} from "../lib/ledger";


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#dce9da",
    backgroundColor: "#f5faf3",
    padding: "16px",
  },
  s2: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  s3: {
    fontSize: "12px",
    color: "#647267",
  },
  s4: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#2f5e3b",
  },
  s5: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "end",
    },
  },
  s6: {
    display: "block",
  },
  s7: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s8: {
    display: "flex",
    height: "48px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    ":focus-within": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s9: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "14px",
    fontSize: "16px",
    fontWeight: 600,
    outline: "none",
    "::placeholder": {
      fontWeight: 400,
      color: "#99a099",
    },
  },
  s10: {
    borderLeftWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#f9fbf8",
    paddingInline: "8px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#68806d",
    outline: "none",
  },
  s11: {
    marginBottom: "12px",
    display: "none",
    textAlign: "center",
    fontSize: "18px",
    color: "#7c8b80",
    "@media (min-width: 640px)": {
      display: "block",
    },
  },
  s12: {
    display: "flex",
    height: "48px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#f5faf3",
  },
  s13: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#397a4b",
  },
  s14: {
    borderLeftWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#edf6eb",
    paddingInline: "8px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#68806d",
    outline: "none",
  },
  s15: {
    display: "flex",
    height: "48px",
    alignItems: "center",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    ":focus-within": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s16: {
    paddingLeft: "14px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#68806d",
  },
  s17: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "8px",
    fontSize: "16px",
    fontWeight: 600,
    outline: "none",
  },
  s18: {
    paddingRight: "14px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#68806d",
  },
  s19: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#7a887d",
  },
  s20: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s21: {
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      justifyContent: "end",
    },
  },
  s22: {
    height: "44px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#607068",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s23: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s24: {
    height: "16px",
    width: "16px",
  },
})
type Props = {
  data: Overview;
  initialFromCurrency?: string;
  personID: string;
  onClose: () => void;
  onSave: (input: CreateCurrencyExchangeInput) => Promise<void>;
};

function defaultRate(
  fromCurrency: string,
  toCurrency: string,
  exchangeRates?: Record<string, number>,
) {
  const directRate = exchangeRates?.[`${fromCurrency}_${toCurrency}`];
  if (directRate && directRate > 0) return directRate / 10000;
  const reverseRate = exchangeRates?.[`${toCurrency}_${fromCurrency}`];
  if (reverseRate && reverseRate > 0) return 10000 / reverseRate;
  if (fromCurrency === "USD" && toCurrency === "AWG") return 1.8;
  if (fromCurrency === "EUR" && toCurrency === "AWG") return 2.1;
  if (fromCurrency === "AWG" && toCurrency === "USD") return 1 / 1.8;
  if (fromCurrency === "AWG" && toCurrency === "EUR") return 1 / 2.1;
  return 1;
}

export default function CurrencyExchangeDialog({
  data,
  initialFromCurrency,
  personID,
  onClose,
  onSave,
}: Props) {
  const person = findPerson(data, personID);
  const options = currencyOptions(data.currentUser.currency);
  const startingFromCurrency =
    initialFromCurrency && options.includes(initialFromCurrency)
      ? initialFromCurrency
      : data.currentUser.currency;
  const startingToCurrency =
    startingFromCurrency === data.currentUser.currency
      ? (options.find((code) => code !== startingFromCurrency) ??
        startingFromCurrency)
      : data.currentUser.currency;
  const [fromAmount, setFromAmount] = useState("");
  const [fromCurrency, setFromCurrency] = useState(startingFromCurrency);
  const [toCurrency, setToCurrency] = useState(startingToCurrency);
  const [rate, setRate] = useState(() =>
    defaultRate(
      startingFromCurrency,
      startingToCurrency,
      data.currentUser.exchangeRates,
    ).toFixed(2),
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fromAmountCents = parseCents(fromAmount);
  const rateValue = Number(rate.replace(",", "."));
  const toAmountCents =
    fromAmountCents !== null && Number.isFinite(rateValue)
      ? Math.round((fromAmountCents / 100) * rateValue * 100)
      : 0;
  const valid =
    Boolean(person) &&
    fromAmountCents !== null &&
    fromAmountCents > 0 &&
    toAmountCents > 0 &&
    Number.isFinite(rateValue) &&
    rateValue > 0 &&
    fromCurrency !== toCurrency;

  function changeFromCurrency(nextCurrency: string) {
    setFromCurrency(nextCurrency);
    setRate(
      defaultRate(nextCurrency, toCurrency, data.currentUser.exchangeRates).toFixed(2),
    );
  }

  function changeToCurrency(nextCurrency: string) {
    setToCurrency(nextCurrency);
    setRate(
      defaultRate(fromCurrency, nextCurrency, data.currentUser.exchangeRates).toFixed(2),
    );
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || fromAmountCents === null || !person) {
      setError(
        fromCurrency === toCurrency
          ? "Choose two different currencies."
          : "Enter a valid amount and exchange rate.",
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        personId: person.id,
        fromAmountCents,
        fromCurrency,
        toAmountCents,
        toCurrency,
        rateBps: Math.round(rateValue * 10000),
      });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "We could not record that exchange.",
      );
      setSaving(false);
    }
  }

  if (!person) return null;

  return (
    <Dialog
      description="Record money exchanged with this person using the rate you agreed on."
      onClose={saving ? () => undefined : onClose}
      title="Record currency exchange"
    >
      <form {...stylex.props(styles.s0)} onSubmit={submit}>
        <div {...stylex.props(styles.s1)}>
          <div {...stylex.props(styles.s2)}>
            <Avatar person={person} size={42} />
            <div>
              <p {...stylex.props(styles.s3)}>Exchange with</p>
              <p {...stylex.props(styles.s4)}>
                {person.name}
              </p>
            </div>
          </div>
        </div>
        <div {...stylex.props(styles.s5)}>
          <label {...stylex.props(styles.s6)}>
            <span {...stylex.props(styles.s7)}>
              You give
            </span>
            <div {...stylex.props(styles.s8)}>
              <input
                autoFocus
                {...stylex.props(styles.s9)}
                data-dialog-autofocus
                inputMode="decimal"
                onChange={(event) => setFromAmount(event.target.value)}
                placeholder="0.00"
                value={fromAmount}
              />
              <select
                aria-label="Currency you give"
                {...stylex.props(styles.s10)}
                onChange={(event) => changeFromCurrency(event.target.value)}
                value={fromCurrency}
              >
                {options.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </label>
          <span {...stylex.props(styles.s11)}>
            →
          </span>
          <label {...stylex.props(styles.s6)}>
            <span {...stylex.props(styles.s7)}>
              You receive
            </span>
            <div {...stylex.props(styles.s12)}>
              <output {...stylex.props(styles.s13)}>
                {toAmountCents > 0 ? centsToInput(toAmountCents) : "0.00"}
              </output>
              <select
                aria-label="Currency you receive"
                {...stylex.props(styles.s14)}
                onChange={(event) => changeToCurrency(event.target.value)}
                value={toCurrency}
              >
                {options.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>
        <label {...stylex.props(styles.s6)}>
          <span {...stylex.props(styles.s7)}>
            Exchange rate
          </span>
          <div {...stylex.props(styles.s15)}>
            <span {...stylex.props(styles.s16)}>
              1 {fromCurrency} =
            </span>
            <input
              {...stylex.props(styles.s17)}
              inputMode="decimal"
              onChange={(event) => setRate(event.target.value)}
              value={rate}
            />
            <span {...stylex.props(styles.s18)}>
              {toCurrency}
            </span>
          </div>
          <p {...stylex.props(styles.s19)}>
            Defaults can be configured from your profile settings.
          </p>
        </label>
        {error && (
          <p
            {...stylex.props(styles.s20)}
            role="alert"
          >
            {error}
          </p>
        )}
        <div {...stylex.props(styles.s21)}>
          <button
            {...stylex.props(styles.s22)}
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            {...stylex.props(styles.s23)}
            disabled={!valid || saving}
            type="submit"
          >
            {saving ? "Recording…" : "Record exchange"}
            <Icon {...stylex.props(styles.s24)} name="check" />
          </button>
        </div>
      </form>
    </Dialog>
  );
}
